import { StaticSiteDataContext } from '../contexts/StaticSiteDataContext';
import {
  ContentText,
  ContentWrapper,
  HeadlineSmall,
  HeadlineMedium,
  Image,
  RichTextContent,
} from './components';
import { ActionLink } from '@minted/fancyclothes';
import React, { useContext, useMemo } from 'react';
import { connect } from 'react-redux';
import { getPhotoByPk, RootState } from '../resources/reduxTypes';
import { Photo, RichTextType } from '../resources/modelTypes';
import { richTextMarginBottomSpaceSwitch } from '../forms/components/RichText/utils';
import { maxWidthImageSize, richTextBottomMargin } from './styles';
import { BLANK_SLATE_DOCUMENT } from '../core/constants';
import isEqual from 'lodash/isEqual';

export type ContentProps = {
  actionLinkProps?: {
    onClick: () => void,
    text: string,
  },
  configuration: {
    imageAlignment: string,
    imageSize: number,
    pageLayout: string,
    showMap: boolean,
    textAlignment: string,
  },
  description?: RichTextType | null,
  photoPk?: number | null,
  image?: string | null,
  title: string | null,
  url?: string,
}

type ContentPropsWithRedux = {
    fullPhoto?: Photo | null,
} & ContentProps;

const Content = ({
  actionLinkProps,
  configuration,
  description,
  fullPhoto,
  image,
  title,
  url,
}: ContentPropsWithRedux) => {
  const {
    isOnlineInvitationSite,
  } = useContext(StaticSiteDataContext);
  // OI fakes the section content to look like page content, so the header
  // needs to match the page header size

  const HeadlineComponent = isOnlineInvitationSite
    ? HeadlineMedium
    : HeadlineSmall;

  const imageToDisplay = useMemo(() => image || fullPhoto?.original || null, [
    fullPhoto?.original,
    image,
  ]);

  const isEmptyParagraph = isEqual(description, BLANK_SLATE_DOCUMENT);
  const richTextContentMb = richTextMarginBottomSpaceSwitch(configuration.imageAlignment, isEmptyParagraph);

  return (
    <ContentWrapper
      imageAlignment={configuration.imageAlignment}
      noVerticalMargin
      textAlignment={configuration.textAlignment}
    >
      <div
        css={maxWidthImageSize}
      >
        <Image
          image={imageToDisplay}
          imageAlignment={configuration.imageAlignment}
          imageSize={configuration.imageSize}
        />
      </div>
      <ContentText textAlignment={configuration.textAlignment}>
        {
          Boolean(title) && (
            <div
              data-cy="subheading"
            >
              <HeadlineComponent imageAlignment={configuration.imageAlignment}>
                {title}
              </HeadlineComponent>
            </div>
          )
        }
        <div
          css={richTextBottomMargin(richTextContentMb)}
          data-cy="body"
        >
          <RichTextContent value={description} />
          {
            url && actionLinkProps && (
              <ActionLink
                target="_blank"
                url={url}
                {...actionLinkProps}
              />
            )
          }
        </div>
      </ContentText>
    </ContentWrapper>
  );
};

const mapStateToProps = (state: RootState, props: ContentProps) => ({
  fullPhoto: getPhotoByPk(state, props.photoPk),
});

// @ts-expect-error Force types for redux/react connection
export default connect(mapStateToProps)(Content) as React.FC<ContentProps>;
